import React, { useCallback } from 'react';
import { DualFacedCardIndicator } from '../inventory/DualFacedCardIndicator';

interface CardImageProps {
  scryfallId?: string | null;
  cardName?: string | null;
  imageUrl?: string | null;
  size?: 'sm' | 'md' | 'lg' | 'fill';
  /** Support for dual-faced cards */
  isDualFaced?: boolean;
  reverseImageUrl?: string | null;
  reverseScryfallId?: string | null;
  /** Current flip state (if managed by parent) */
  isFlipped?: boolean;
  onFlip?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Displays a card image from Scryfall's CDN
 * Uses provided imageUrl if available, falls back to scryfallId
 * 
 * Features:
 * - Direct image loading (Scryfall CDN has no rate limits)
 * - Dual-faced card support with flip indicator button
 */
export const CardImage: React.FC<CardImageProps> = ({ 
  scryfallId, 
  cardName, 
  imageUrl, 
  size = 'md',
  isDualFaced = false,
  reverseImageUrl,
  reverseScryfallId,
  isFlipped = false,
  onFlip,
}) => {
  // Flip handler for dual-faced cards
  const handleFlip = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onFlip?.(e);
    },
    [onFlip]
  );

  const scryfallSize = size === 'sm' ? 'small' : size === 'lg' ? 'large' : 'normal';
  
  const buildScryfallCdnUrl = (id: string, variant: string, face: 'front' | 'back' = 'front'): string => {
    const normalized = id.toLowerCase();
    const first = normalized[0] || '0';
    const second = normalized[1] || '0';
    return `https://cards.scryfall.io/${variant}/${face}/${first}/${second}/${normalized}.jpg`;
  };
  
  const normalizeScryfallUrl = (url: string | null | undefined): string | undefined => {
    if (!url) return undefined;
    if (!url.includes('scryfall')) return url;

    const face: 'front' | 'back' = url.includes('face=back') || url.includes('/back/') ? 'back' : 'front';
    const uuidMatch = url.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    if (uuidMatch) {
      return buildScryfallCdnUrl(uuidMatch[0], scryfallSize, face);
    }

    return scryfallId ? buildScryfallCdnUrl(scryfallId, scryfallSize, face) : url;
  };

  // Determine the final image URL to use
  let finalImageUrl = normalizeScryfallUrl(imageUrl);
  if (!finalImageUrl && scryfallId) {
    finalImageUrl = buildScryfallCdnUrl(scryfallId, scryfallSize, 'front');
  }

  // If flipped, prefer the reverse image (URL or scryfall id)
  if (isFlipped) {
    if (reverseImageUrl) {
      finalImageUrl = normalizeScryfallUrl(reverseImageUrl);
    } else if (reverseScryfallId) {
      finalImageUrl = buildScryfallCdnUrl(reverseScryfallId, scryfallSize, 'back');
    }
  }

  const sizeMap: Record<'sm' | 'md' | 'lg', { width: string; height: string }> = {
    sm: { width: '120px', height: '168px' },
    md: { width: '280px', height: '392px' },
    lg: { width: '336px', height: '469px' },
  };

  const dimensions = size === 'fill'
    ? { width: '100%', height: '100%' }
    : sizeMap[size];

  const showShadow = size !== 'fill';
  const imageFit = size === 'fill' ? 'cover' : 'contain';
  const imageRadius = size === 'fill' ? '0' : '8px';
  const imageScale = size === 'fill' ? 'scale(1.02)' : 'none';

  if (!finalImageUrl) {
    return (
      <div
        style={{
          ...dimensions,
          backgroundColor: '#e0e0e0',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          fontSize: '12px',
          border: '1px solid #ccc',
        }}
      >
        No image
      </div>
    );
  }

  return (
    <div
      style={{ display: 'flex', justifyContent: 'center', width: '100%', position: 'relative' }}
    >
      <img
        key={`${scryfallId || imageUrl}-${isFlipped}`}
        src={finalImageUrl}
        alt={cardName || 'Card image'}
        style={{
          ...dimensions,
          borderRadius: imageRadius,
          objectFit: imageFit,
          boxShadow: showShadow ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
          imageRendering: 'crisp-edges',
          transform: imageScale,
        }}
      />
      
      {/* Dual-faced card flip indicator */}
      {isDualFaced && (reverseImageUrl || reverseScryfallId) && (
        <DualFacedCardIndicator isFlipped={isFlipped} onFlip={handleFlip} />
      )}
    </div>
  );
};
